import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Keyable from '../../../../types/keyable';
import httpErrorHandling from '../../../../utils/httpErrorHandling';
import { confirmAdditionalInfoStep } from './additionalInfoRequest';
import { OnboardingStepState } from '../../onboardingSlice';
import IPicture from '../../../../common/interfaces/IPicture';

export interface CertificationState extends Keyable {
  name: string;
  organisation: string;
  year: string;
}

export interface AwardState extends Keyable {
  name: string;
  year: string;
}

export interface AffiliationState extends Keyable {
  description: string;
}

export interface TeamMemberState extends Keyable {
  picture?: IPicture;
  name: string;
  position: string;
  contact: string;
}

export interface TestimonialState extends Keyable {
  description: string;
}

export interface AdditionalInfoState {
  certifications: CertificationState[];
  awards: AwardState[];
  affiliations: AffiliationState[];
  testimonials: TestimonialState[];
  team: TeamMemberState[];
}

const initialState: AdditionalInfoState & OnboardingStepState = {
  error: '',
  inProgress: false,
  isDone: false,
  certifications: [],
  awards: [],
  affiliations: [],
  testimonials: [],
  team: [],
  doRedirectFurther: false,
};
export const additionalInfoSlice = createSlice({
  name: 'additionalInfo',
  initialState,
  reducers: {
    setAdditionalInfoStateDefault: () => {
      return { ...initialState };
    },
    startProcessingAdditionalInfo: state => {
      state.inProgress = true;
    },
    submitCertifications: (state, action: PayloadAction<CertificationState[]>) => {
      state.certifications = action.payload;
    },
    submitAwards: (state, action: PayloadAction<AwardState[]>) => {
      state.awards = action.payload;
    },
    submitAffiliations: (state, action: PayloadAction<AffiliationState[]>) => {
      state.affiliations = action.payload;
    },
    submitTestimonials: (state, action: PayloadAction<TestimonialState[]>) => {
      state.testimonials = action.payload;
    },
    submitTeam: (state, action) => {
      state.team = action.payload;
    },
    submitAdditionalInfo: state => {
      state.doRedirectFurther = false;
      state.inProgress = false;
      state.isDone = true;
    },
  },
  extraReducers: builder => {
    builder.addCase(confirmAdditionalInfoStep.fulfilled, state => {
      state.error = '';
      state.doRedirectFurther = true;
    });
    builder.addCase(confirmAdditionalInfoStep.pending, state => {
      state.error = '';
    });
    builder.addCase(confirmAdditionalInfoStep.rejected, (state, action) => {
      state.error = httpErrorHandling(action);
    });
  },
});

export const {
  setAdditionalInfoStateDefault,
  startProcessingAdditionalInfo,
  submitCertifications,
  submitAwards,
  submitAffiliations,
  submitTestimonials,
  submitAdditionalInfo,
  submitTeam,
} = additionalInfoSlice.actions;

export default additionalInfoSlice.reducer;
